import type { ButtonHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md";

/**
 * `primary` fills with --accent-strong rather than --accent: the accent is 4.35:1
 * against white, which is fine for a border or a dot but under the 4.5:1 a button
 * label needs. Hover deepens the fill instead of dropping opacity — a translucent
 * button picks up whatever is behind it, which on a table row is the hover colour.
 */
const VARIANTS: Record<Variant, string> = {
  primary: "bg-bank-accent-strong text-bank-on-accent hover:bg-bank-accent-hover",
  secondary: "border border-bank-border-strong bg-bank-surface hover:bg-bank-surface-2",
  ghost: "hover:bg-bank-surface-2",
  danger: "border border-bank-bad text-bank-bad-ink hover:bg-bank-bad-wash",
};

/** One control height per size, shared with Input and Combobox so a filter row lines up. */
const SIZES: Record<Size, string> = {
  sm: "h-7 px-2.5 text-xs",
  md: "h-8 px-3 text-sm",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

export function Button({ className, variant = "secondary", size = "md", ...props }: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors",
        "focus-visible:ring-bank-accent focus-visible:ring-2 focus-visible:ring-offset-1",
        "focus-visible:ring-offset-bank-surface focus-visible:outline-none",
        "disabled:pointer-events-none disabled:opacity-45",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    />
  );
}
